import {
  listForumModerationFn,
  moderateForumQuestionFn,
  updateForumEditorialFn,
} from "@/actions/tags/handler";
import { sharedContentTopics } from "@/api/db/schema/content";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import {
  Archive,
  CheckCircle2,
  ExternalLink,
  Merge,
  Pencil,
  Search,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/forum")({
  beforeLoad: ({ context }) => {
    if (
      !context.user ||
      (context.user.role !== "admin" && context.user.role !== "author")
    )
      throw redirect({ to: "/" });
  },
  component: ForumModerationPage,
});

function ForumModerationPage() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<
    "all" | "pending_review" | "published" | "archived" | "merged"
  >("pending_review");
  const [mergeTargets, setMergeTargets] = useState<Record<string, string>>({});
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null,
  );
  const [editorTopics, setEditorTopics] = useState<string[]>([]);
  const [editorCtaHref, setEditorCtaHref] = useState("");
  const [editorCtaLabel, setEditorCtaLabel] = useState("");
  const questions = useQuery({
    queryKey: ["forum-moderation"],
    queryFn: () => listForumModerationFn(),
  });
  const mutation = useMutation({
    mutationFn: ({
      tagId,
      next,
      mergedIntoId,
    }: {
      tagId: string;
      next: "published" | "archived" | "pending_review" | "merged";
      mergedIntoId?: string;
    }) =>
      moderateForumQuestionFn({
        data: { tagId, status: next, mergedIntoId: mergedIntoId || null },
      }),
    onSuccess: () => {
      void questions.refetch();
      toast.success("Soru durumu güncellendi.");
    },
    onError: (error) => toast.error(error.message),
  });
  const saveEditorial = useMutation({
    mutationFn: () =>
      updateForumEditorialFn({
        data: {
          tagId: editingQuestionId || "",
          topics: editorTopics as never,
          ctaHref: editorCtaHref || null,
          ctaLabel: editorCtaLabel || null,
        },
      }),
    onSuccess: () => {
      setEditingQuestionId(null);
      void questions.refetch();
      toast.success("Konu bağlantıları kaydedildi.");
    },
    onError: (error) => toast.error(error.message),
  });
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("tr-TR");
    return (questions.data?.questions || []).filter((question) => {
      if (status !== "all" && question.moderationStatus !== status)
        return false;
      return (
        !normalized ||
        `${question.tagTitle} ${question.tagContent} ${question.creater.userName}`
          .toLocaleLowerCase("tr-TR")
          .includes(normalized)
      );
    });
  }, [query, questions.data?.questions, status]);

  return (
    <main className="container mx-auto max-w-6xl px-4 py-24">
      <h1 className="font-bold text-3xl">Forum Moderasyonu</h1>
      <p className="mt-2 text-muted-foreground">
        Eski ve yeni soruları ayıklayın; yalnız güçlü içerikleri arama
        motorlarına açın.
      </p>
      <div className="mt-7 grid gap-3 md:grid-cols-[1fr_auto]">
        <div className="relative">
          <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Soru ara"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(
            [
              "pending_review",
              "published",
              "archived",
              "merged",
              "all",
            ] as const
          ).map((value) => (
            <Button
              key={value}
              size="sm"
              variant={status === value ? "default" : "outline"}
              onClick={() => setStatus(value)}
            >
              {value === "pending_review"
                ? "İncelenecek"
                : value === "published"
                  ? "Yayında"
                  : value === "archived"
                    ? "Arşiv"
                    : value === "merged"
                      ? "Birleştirilen"
                      : "Tümü"}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-6 grid gap-4">
        {filtered.map((question) => (
          <Card key={question.tagId}>
            <CardContent className="p-5">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                <div>
                  <div className="flex flex-wrap gap-2">
                    <Badge>{question.category}</Badge>
                    <Badge variant="outline">{question.moderationStatus}</Badge>
                    {question.hasExpertAnswer && (
                      <Badge className="bg-blue-600">Uzman yanıtı var</Badge>
                    )}
                    {question.topics.map((topic) => (
                      <Badge key={topic} variant="secondary">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                  <h2 className="mt-3 font-bold text-xl">
                    {question.tagTitle}
                  </h2>
                  <p className="mt-2 line-clamp-2 text-muted-foreground text-sm">
                    {question.tagContent}
                  </p>
                  <p className="mt-2 text-muted-foreground text-xs">
                    {question.creater.userName} · {question.messageCount} yanıt
                    · {question.viewCount} görüntülenme
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button size="sm" variant="outline" asChild>
                    <Link
                      to="/forum/$tagId"
                      params={{ tagId: question.slug || question.tagId }}
                      target="_blank"
                    >
                      <ExternalLink className="mr-1 h-4 w-4" />
                      Aç
                    </Link>
                  </Button>
                  {question.moderationStatus !== "published" && (
                    <Button
                      size="sm"
                      onClick={() =>
                        mutation.mutate({
                          tagId: question.tagId,
                          next: "published",
                        })
                      }
                    >
                      <CheckCircle2 className="mr-1 h-4 w-4" />
                      Yayınla
                    </Button>
                  )}
                  {question.moderationStatus !== "archived" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() =>
                        mutation.mutate({
                          tagId: question.tagId,
                          next: "archived",
                        })
                      }
                    >
                      <Archive className="mr-1 h-4 w-4" />
                      Arşivle
                    </Button>
                  )}
                  {question.moderationStatus === "archived" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        mutation.mutate({
                          tagId: question.tagId,
                          next: "pending_review",
                        })
                      }
                    >
                      İncelemeye al
                    </Button>
                  )}
                  {question.moderationStatus !== "merged" && (
                    <div className="flex w-full items-center gap-2 pt-2 md:w-auto md:min-w-72">
                      <Select
                        value={mergeTargets[question.tagId] || ""}
                        onValueChange={(value) =>
                          setMergeTargets((current) => ({
                            ...current,
                            [question.tagId]: value,
                          }))
                        }
                      >
                        <SelectTrigger className="h-9 flex-1">
                          <SelectValue placeholder="Ana soruyu seç" />
                        </SelectTrigger>
                        <SelectContent>
                          {(questions.data?.questions || [])
                            .filter(
                              (candidate) =>
                                candidate.tagId !== question.tagId &&
                                candidate.moderationStatus === "published",
                            )
                            .map((candidate) => (
                              <SelectItem
                                key={candidate.tagId}
                                value={candidate.tagId}
                              >
                                {candidate.tagTitle}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!mergeTargets[question.tagId]}
                        onClick={() =>
                          mutation.mutate({
                            tagId: question.tagId,
                            next: "merged",
                            mergedIntoId: mergeTargets[question.tagId],
                          })
                        }
                      >
                        <Merge className="mr-1 h-4 w-4" />
                        Birleştir
                      </Button>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingQuestionId(question.tagId);
                      setEditorTopics(question.topics);
                      setEditorCtaHref(question.ctaHref || "");
                      setEditorCtaLabel(question.ctaLabel || "");
                    }}
                  >
                    <Pencil className="mr-1 h-4 w-4" /> İçerik bağlantıları
                  </Button>
                </div>
              </div>
              {editingQuestionId === question.tagId && (
                <div className="mt-5 space-y-4 border-t pt-4">
                  <div className="flex flex-wrap gap-2">
                    {sharedContentTopics.map((topic) => (
                      <Button
                        key={topic}
                        size="sm"
                        variant={
                          editorTopics.includes(topic) ? "default" : "outline"
                        }
                        onClick={() =>
                          setEditorTopics((current) =>
                            current.includes(topic)
                              ? current.filter((item) => item !== topic)
                              : [...current, topic],
                          )
                        }
                      >
                        {topic}
                      </Button>
                    ))}
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Input
                      value={editorCtaHref}
                      onChange={(event) => setEditorCtaHref(event.target.value)}
                      placeholder="CTA adresi: /essay/1rm-calculator"
                    />
                    <Input
                      value={editorCtaLabel}
                      onChange={(event) =>
                        setEditorCtaLabel(event.target.value)
                      }
                      placeholder="CTA metni"
                    />
                  </div>
                  <Button size="sm" onClick={() => saveEditorial.mutate()}>
                    Kaydet
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </main>
  );
}
